import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Download as DownloadIcon,
  People as PeopleIcon,
  ChildCare as ChildCareIcon,
  EventAvailable as VisitIcon,
  FilterAltOff as FilterOffIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { reportsAPI, locationAPI } from '../../services/api';
import { SummaryCard, nameInitial, downloadBlob, textFieldSx, FORM_TYPE_TABS, VisitBadge } from '../components/reportUi';

// The महिना filter defaults to the current month on every landing (and on
// रीसेट) rather than an empty "all time" — so a supervisor/admin opening
// this report always sees this month's data first, instead of a blank/huge
// unfiltered table they'd have to filter down themselves.
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

function SupervisorReports() {
  const navigate = useNavigate();
  const location = useLocation();
  const [districts, setDistricts] = useState([]);
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [phcOptions, setPhcOptions] = useState([]);
  const [subCentreOptions, setSubCentreOptions] = useState([]);

  const [filters, setFilters] = useState({
    formType: 'all',
    month: getCurrentMonth(),
    dateFrom: '',
    dateTo: '',
    district: '',
    taluka: '',
    phc: '',
    subCentre: '',
    ashaWorker: '',
    status: '',
  });

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalAshaWorkers: 0, totalMothers: 0, totalVisits: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    locationAPI.getDistricts().then((res) => {
      setDistricts(res.data);
      // This report has no District filter of its own (the app's master
      // data is Thane-only in practice) — default straight to Thane so the
      // Taluka dropdown below has something to show without an extra,
      // redundant selector.
      const thane = res.data.find((d) => d.name_en === 'Thane') || res.data[0];
      if (thane) {
        setFilters((f) => (f.district ? f : { ...f, district: thane._id }));
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const d = districts.find((x) => x._id === filters.district);
    setTalukaOptions(d?.talukas || []);
  }, [filters.district, districts]);

  useEffect(() => {
    if (!filters.taluka) { setPhcOptions([]); return; }
    locationAPI.getPHCs({ taluka_id: filters.taluka }).then((res) => setPhcOptions(res.data)).catch(() => {});
  }, [filters.taluka]);

  useEffect(() => {
    if (!filters.phc) { setSubCentreOptions([]); return; }
    locationAPI.getSubCentres({ phc_id: filters.phc }).then((res) => setSubCentreOptions(res.data)).catch(() => {});
  }, [filters.phc]);

  const buildParams = useCallback(() => {
    const params = { form_type: filters.formType };
    if (filters.month) params.month = filters.month;
    else {
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
    }
    if (filters.taluka) params.taluka_id = filters.taluka;
    if (filters.phc) params.phc_id = filters.phc;
    if (filters.subCentre) params.sub_centre_id = filters.subCentre;
    if (filters.ashaWorker) params.asha_worker_id = filters.ashaWorker;
    if (filters.status) params.is_active = filters.status === 'active';
    return params;
  }, [filters]);

  const fetchReport = useCallback(() => {
    setLoading(true);
    setError('');
    reportsAPI.getAshaVisitReport(buildParams())
      .then((res) => {
        setRows(res.data.rows);
        setSummary(res.data.summary);
      })
      .catch(() => setError('अहवाल लोड करताना त्रुटी आली'))
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // महिना defaults to the current month (see getCurrentMonth above) — that
  // default alone shouldn't count as an "active filter" the user needs to
  // clear, only a month they've actually changed away from it should.
  const activeFilterCount = Object.entries(filters).filter(([k, v]) => {
    if (k === 'district' || k === 'formType') return false;
    if (k === 'month' && v === getCurrentMonth()) return false;
    return !!v;
  }).length;

  const resetFilters = () => setFilters((f) => ({
    formType: f.formType, month: getCurrentMonth(), dateFrom: '', dateTo: '', district: f.district, taluka: '', phc: '', subCentre: '', ashaWorker: '', status: '',
  }));

  // Navigates to WorkerVisitDetail.js's own route (a full page now, not a
  // dialog) — carrying the current formType + date filter as query params so
  // that page's own fetch is scoped identically to what this row shows.
  const openDetail = (row) => {
    const params = new URLSearchParams();
    params.set('name', row.ashaWorkerName || '');
    params.set('formType', filters.formType);
    if (filters.month) {
      params.set('month', filters.month);
    } else {
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
    }
    navigate(`${location.pathname}/${row.ashaWorkerId}?${params.toString()}`);
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await reportsAPI.exportAshaVisitReport({ ...buildParams(), format });
      downloadBlob(res.data, `asha_visit_report_${filters.formType}.${format}`);
    } catch {
      setError('एक्सपोर्ट करताना त्रुटी आली');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: 'grey.900', letterSpacing: '-0.01em' }}>आशा कार्यकर्ता भेटी अहवाल</Typography>
        <Typography sx={{ fontSize: '0.875rem', color: 'grey.600', mt: 0.25 }}>
          प्रत्येक आशा कार्यकर्तीने पूर्ण केलेल्या भेटी व माता संख्या — निवडलेल्या फिल्टरनुसार
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs
        value={filters.formType}
        onChange={(e, v) => setFilters((f) => ({ ...f, formType: v, ashaWorker: '' }))}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2.5, borderBottom: '1px solid', borderColor: 'grey.200' }}
      >
        {FORM_TYPE_TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} sx={{ fontWeight: 700, textTransform: 'none' }} />)}
      </Tabs>

      {/* Summary */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <SummaryCard title="एकूण आशा कार्यकर्त्या" value={summary.totalAshaWorkers} icon={PeopleIcon} color="primary" />
        <SummaryCard title="एकूण माता" value={summary.totalMothers} icon={ChildCareIcon} color="secondary" />
        <SummaryCard title={filters.month ? 'या महिन्यातील भेटी' : 'एकूण भेटी'} value={summary.totalVisits} icon={VisitIcon} color="success" />
      </Box>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 3, borderColor: 'grey.200' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'grey.800' }}>फिल्टर</Typography>
          {activeFilterCount > 0 && (
            <Button size="small" startIcon={<FilterOffIcon fontSize="small" />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 600 }}>
              फिल्टर साफ करा ({activeFilterCount})
            </Button>
          )}
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              fullWidth size="small" label="महिना" type="month"
              InputLabelProps={{ shrink: true }}
              value={filters.month}
              onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value, dateFrom: '', dateTo: '' }))}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              fullWidth size="small" label="पासून" type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom}
              disabled={!!filters.month}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              fullWidth size="small" label="पर्यंत" type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo}
              disabled={!!filters.month}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              select fullWidth size="small" label="तालुका"
              value={filters.taluka}
              onChange={(e) => setFilters((f) => ({ ...f, taluka: e.target.value, phc: '', subCentre: '' }))}
              sx={textFieldSx}
            >
              <MenuItem value="">सर्व</MenuItem>
              {talukaOptions.map((t) => <MenuItem key={t._id} value={t._id}>{t.name_mr}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={1.5}>
            <TextField
              select fullWidth size="small" label="PHC"
              value={filters.phc}
              disabled={!filters.taluka}
              onChange={(e) => setFilters((f) => ({ ...f, phc: e.target.value, subCentre: '' }))}
              sx={textFieldSx}
            >
              <MenuItem value="">सर्व</MenuItem>
              {phcOptions.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <TextField
              select fullWidth size="small" label="उप-केंद्र"
              value={filters.subCentre}
              disabled={!filters.phc}
              onChange={(e) => setFilters((f) => ({ ...f, subCentre: e.target.value }))}
              sx={textFieldSx}
            >
              <MenuItem value="">सर्व</MenuItem>
              {subCentreOptions.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={8} sm={6} md={3}>
            <TextField
              select fullWidth size="small" label="आशा कार्यकर्ता"
              value={filters.ashaWorker}
              onChange={(e) => setFilters((f) => ({ ...f, ashaWorker: e.target.value }))}
              sx={textFieldSx}
            >
              <MenuItem value="">सर्व</MenuItem>
              {rows.map((r) => <MenuItem key={r.ashaWorkerId} value={r.ashaWorkerId}>{r.ashaWorkerName}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={4} sm={3} md={1.5}>
            <TextField
              select fullWidth size="small" label="स्थिती"
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              sx={textFieldSx}
            >
              <MenuItem value="">सर्व</MenuItem>
              <MenuItem value="active">सक्रिय</MenuItem>
              <MenuItem value="inactive">निष्क्रिय</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Export bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.25,
        mb: 2,
      }}>
        <Button startIcon={<DownloadIcon />} variant="outlined" size="small" disabled={exporting} onClick={() => handleExport('csv')} sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600 }}>
          CSV
        </Button>
        <Button startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />} variant="contained" size="small" disabled={exporting} onClick={() => handleExport('xlsx')} sx={{ textTransform: 'none', borderRadius: 1.5, fontWeight: 600, boxShadow: 'none' }}>
          Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3, borderColor: 'grey.200' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.50', fontWeight: 700, color: 'grey.700', fontSize: '0.78rem', borderBottom: '1px solid', borderColor: 'grey.200' } }}>
                <TableCell>आशा कार्यकर्ता</TableCell>
                <TableCell>तालुका</TableCell>
                <TableCell>PHC</TableCell>
                <TableCell>उप-केंद्र</TableCell>
                <TableCell align="center">माता</TableCell>
                <TableCell align="center">भेटी</TableCell>
                <TableCell>शेवटची भेट</TableCell>
                <TableCell>स्थिती</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: 'grey.500' }}>
                    निवडलेल्या फिल्टरसाठी कोणतीही नोंद सापडली नाही
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => (
                <TableRow
                  key={row.ashaWorkerId}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:last-child td': { borderBottom: 0 },
                    transition: 'background-color 0.12s ease',
                  }}
                  onClick={() => openDetail(row)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.88rem', fontWeight: 700, bgcolor: 'primary.lighter', color: 'primary.main' }}>
                        {nameInitial(row.ashaWorkerName)}
                      </Avatar>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{row.ashaWorkerName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'grey.700', fontSize: '0.82rem' }}>{row.taluka}</TableCell>
                  <TableCell sx={{ color: 'grey.700', fontSize: '0.82rem' }}>{row.phc}</TableCell>
                  <TableCell sx={{ color: 'grey.700', fontSize: '0.82rem' }}>{row.subCentre}</TableCell>
                  <TableCell align="center">
                    <Chip label={row.totalMothers} size="small" variant="outlined" sx={{ fontWeight: 700, minWidth: 42 }} />
                  </TableCell>
                  <TableCell align="center">
                    <VisitBadge value={row.totalVisits} />
                  </TableCell>
                  <TableCell sx={{ color: 'grey.600', fontSize: '0.8rem' }}>{row.lastVisitDate || '-'}</TableCell>
                  <TableCell>
                    <Chip label={row.isActive ? 'सक्रिय' : 'निष्क्रिय'} color={row.isActive ? 'success' : 'default'} size="small" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">
                    <ChevronRightIcon sx={{ color: 'grey.400' }} fontSize="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}

export default SupervisorReports;
